import React from 'react';

import type { ContactCardBodyCadenceStep, Stage } from '@poseidon/components/ContactCardBodyPure';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import { cn } from '@poseidon/components/cn';
import ContactCardBodyPure from '@poseidon/components/ContactCardBodyPure';
import { ContactCardContainer } from '@poseidon/components/ContactCardContainer';
import { ContactCardImageWithOwner } from '@poseidon/components/ContactCardImage';
import { type ContactCardOrgRowOrg } from '@poseidon/components/ContactCardOrgRowPure';
import { contactNameFlat } from '@poseidon/components/contactUtils';
import { CheckboxIconButton } from '@poseidon/components/Input2';

import { type ContactCardBodyCadence, type ContactCardBodyContact } from '~/components/ContactCard/ContactCardTypes';

export type ContactCardUser = {
  liConnected: boolean | null;
  liConnectedAt: Date | null;
  name: string | null;
  email: string | null;
  profilePicture: string | null;
  disqualified?: boolean;
};

export interface ContactCardCadenceActionsReminder {
  id: string;
  body: string | null;
  planRunAt: Date;
  /** The sequence that scheduled this to-do (a sequence `call` / `reminder` step), or null when it came
   *  from a campaign step or was scheduled by hand. See `SvrGlobalReminder.sequence`. */
  sequenceName?: string | null;
}

export interface ContactCardPureProps {
  contact: ContactCardBodyContact;
  contactResponse?: { id: string };
  contactUser?: ContactCardUser | null;
  authedUserLiConnected?: boolean | null;
  authedUserLiConnectedAt?: Date | null;
  index?: number;
  firstReminder?: ContactCardCadenceActionsReminder | null;
  org: ContactCardOrgRowOrg | null;
  cadence?: ContactCardBodyCadence;
  cadenceSteps?: ContactCardBodyCadenceStep[] | null;
  className?: string;
  show?: 'reminder' | 'step';
  isChecked: boolean;
  isActive: boolean;
  stages: Stage[];
  stagesLoading?: boolean;
  stageUpdating: boolean;
  /** When true, render the contact's trimmed cutout as a standing sticker instead of the circular photo (team cutout flag). */
  showCutout?: boolean;
  /** An async enrichment run is live for this contact (docs/WATERFALL_ENRICHMENT.md §8). */
  enriching?: boolean;

  onClick: () => unknown;
  onStageChange: (stageId: string | null) => unknown;
  onToggleChecked?: (
    data: { contactId: string; cadenceId?: string | undefined; reminderId?: string | undefined },
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => unknown;
  canNotRunStepTypes: Set<CadenceStepType> | undefined;
}

export const ContactCardPure = ({
  contact,
  contactResponse,
  contactUser,
  authedUserLiConnected,
  authedUserLiConnectedAt,
  firstReminder,
  cadence,
  cadenceSteps,
  org,
  onToggleChecked,
  index,
  canNotRunStepTypes,
  className,
  show,
  onClick,
  isChecked,
  isActive,
  stages,
  stagesLoading,
  stageUpdating,
  onStageChange,
  showCutout,
  enriching,
}: ContactCardPureProps) => {
  return (
    <div className={cn('group flex gap-3.5', className)}>
      {(onToggleChecked || index != null) && (
        <div>
          {onToggleChecked && (
            <CheckboxIconButton
              checked={isChecked}
              onClick={(e) =>
                onToggleChecked({ contactId: contact.id, cadenceId: cadence?.id, reminderId: firstReminder?.id }, e)
              }
            />
          )}
          {index != null && (
            <div className="text-center text-xs text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100">
              {index}
            </div>
          )}
        </div>
      )}
      <ContactCardContainer isChecked={isChecked} active={isActive} onClick={onClick}>
        {enriching && (
          <span className="absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600 dark:bg-blue-950 dark:text-blue-300">
            <span className="size-1.5 animate-pulse rounded-full bg-blue-500" />
            Enriching…
          </span>
        )}
        <ContactCardImageWithOwner
          name={contactNameFlat({ first_name: contact.firstName ?? null, last_name: contact.lastName ?? null })}
          firstName={contact.firstName}
          lastName={contact.lastName}
          imageSrc={contact.photoUrl}
          cutoutSrc={contact.cutoutUrl}
          showCutout={showCutout}
          orgPictureUrl={org?.pictureUrl}
          cadenceName={cadence?.name}
          showContactUser={contactUser != null}
          contactUserProfilePicture={contactUser?.profilePicture}
          contactUserLiConnected={contactUser?.liConnected}
          contactUserLiConnectedAt={contactUser?.liConnectedAt}
          contactUserName={contactUser?.name}
          contactUserEmail={contactUser?.email}
          authedUserLiConnected={authedUserLiConnected}
          authedUserLiConnectedAt={authedUserLiConnectedAt}
          signalsComputed={contact.signalsComputed}
          triggeredSignals={contact.triggeredSignals}
        />
        <ContactCardBodyPure
          name={contactNameFlat({ first_name: contact.firstName ?? null, last_name: contact.lastName ?? null })}
          honorificTitle={contact.displayHonorific ? (contact.honorificTitle ?? undefined) : undefined}
          liOpenLink={contact.liOpenLink}
          liConnected={contactUser?.liConnected}
          disqualified={contactUser?.disqualified}
          orgName={org?.name ?? contact.companyName ?? undefined}
          orgPictureUrl={org?.pictureUrl}
          title={contact.title}
          tags={contact.tags}
          triggeredSignals={contact.triggeredSignals}
          hasResponse={contactResponse != null}
          cadenceName={cadence?.name}
          cadenceIsActive={cadence?.isActive}
          cadenceState={cadence?.state}
          cadenceRunning={cadence?.running}
          cadenceNextStepTypes={cadence?.nextStepTypes}
          cadenceInterruptMessage={cadence?.interruptMessage}
          firstReminderBody={firstReminder?.body}
          firstReminderDate={firstReminder?.planRunAt}
          firstReminderSequenceName={firstReminder?.sequenceName}
          canNotRunStepTypes={canNotRunStepTypes}
          cadenceSteps={cadenceSteps}
          show={show}
          stages={stages}
          stageId={contact.stageId}
          stagesLoading={stagesLoading}
          stageUpdating={stageUpdating}
          onStageChange={onStageChange}
          phone={contact.phone}
          phoneVerified={contact.phoneVerified}
          professionalEmail={contact.professionalEmail}
          professionalEmailVerified={contact.professionalEmailVerified}
        />
      </ContactCardContainer>
    </div>
  );
};
